import { t } from "@/context/language";
import { trackEvent } from "@/lib/dataLayer";
import { cn } from "@/utils";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { SolutionChatsCard } from "./solutionChatsCard";
import { SolutionInterestCard } from "./solutionInterestCard";
import { SolutionMessagesCard } from "./solutionMessagesCard";

// Ilustração de cada solução; os textos (título/descrição) vêm das traduções.
const SOLUTION_CARDS = [
  SolutionMessagesCard,
  SolutionChatsCard,
  SolutionInterestCard,
];

export function Solutions() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const solutionsData = t.solutions.items.map((item, i) => ({
    ...item,
    Card: SOLUTION_CARDS[i],
  }));

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || firedRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          trackEvent({ event: "view_pricing", section: "solutions" });
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className="flex flex-col gap-16 sm:gap-20 lg:gap-28"
    >
      <h2 className="sr-only">{t.solutions.srHeading}</h2>

      {solutionsData.map((item, index) => {
        const isEven = index % 2 === 0;
        return (
          <motion.div
            key={index}
            className="text-left lg:p-0"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div
              className={cn(
                "flex w-full flex-1 flex-col items-center justify-between gap-4 lg:gap-0",
                isEven ? "lg:flex-row" : "lg:flex-row-reverse"
              )}
            >
              <motion.div
                className="w-full space-y-4 lg:w-[367px] lg:space-y-6"
                initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-title-sm lg:text-heading-sm text-black">
                  {item.title}
                </h3>
                <p className="text-base leading-relaxed text-gray-700 lg:text-lg">
                  {item.description}
                </p>
              </motion.div>

              <motion.div
                className={cn(
                  "bg-secondary mb-6 flex h-auto w-full items-center justify-center rounded-xl border-gray-200 p-4 shadow-lg sm:p-8 lg:mb-0 lg:h-[374px] lg:w-[486px]",
                  !isEven && "lg:col-start-1"
                )}
                initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <item.Card />
              </motion.div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
